import { MenuListItem, StatusBadge } from '@nexus/ui';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatRelative } from '../i18n/format';
import { useModules } from '../modules/context';
import { TONE_STATUS, type ApprovalItem } from './api';

/**
 * A record waiting for a decision of the person, as a link to the record: module, requester, age
 * of the request and state. The decision is taken on the record (docs/architecture/workflows.md).
 */
export function ApprovalEntry({ item }: { item: ApprovalItem }) {
  const { t, i18n } = useTranslation();
  const module = useModules().find((m) =>
    (m.manifest.workflows ?? []).some((w) => w.entity === item.entity),
  )?.manifest;
  const declaration = module?.workflows?.find((w) => w.entity === item.entity);
  const state = declaration?.states.find((s) => s.key === item.state);
  const at = new Date(item.enteredAt);
  return (
    <MenuListItem
      icon={module?.icon}
      href={item.path}
      wrapDescription
      description={
        <>
          {[
            module && t(module.name),
            item.requestedBy?.displayName &&
              t('approvals.requestedBy', { name: item.requestedBy.displayName }),
          ]
            .filter(Boolean)
            .map((part) => `${part} · `)}
          <time dateTime={item.enteredAt} title={formatDateTime(at, i18n.language)}>
            {formatRelative(at, i18n.language, t('common.relative.justNow'))}
          </time>
        </>
      }
      trailing={
        state && (
          <StatusBadge status={TONE_STATUS[state.tone ?? 'neutral']}>{t(state.label)}</StatusBadge>
        )
      }
    >
      {item.title ?? t('approvals.untitled')}
    </MenuListItem>
  );
}
